import type { CSSProperties } from "react";
import type { Observation } from "@/types/agent.type";

export const OBSERVATION_TYPES = {
  REFERENCE_CONTENT: "reference.content",
  REFERENCE_VIDEO_ANALYSIS: "reference.video_analysis",
  REFERENCE_IMAGE_ANALYSIS: "reference.image_analysis",
  COMPETITOR_POST: "competitor.post",
  COMPETITOR_PROFILE: "competitor.profile",
  COMPETITOR_IDENTITY: "competitor.identity",
  ECHO_HELLO: "echo.hello",
  /** Field-reporter evidence: a web citation projected into the observation shape
   *  (payload {title, gist, host}, sourceUrl = the cited url, observedAt = the
   *  signal's createdAt). Every signal the reporter files is grounded in these. */
  REPORT_CITATION: "report.citation",
} as const;

export type ReferenceEnrichment = {
  status?: string;
  mediaKind?: string;
  analysisObservationId?: string;
  s3Key?: string;
  error?: string;
  updatedAt?: string;
};

export type ReferenceContentPayload = {
  platform?: string;
  url?: string;
  caption?: string;
  author?: string;
  relevanceReason?: string;
  searchQuery?: string;
  mediaType?: string;
  enrichment?: ReferenceEnrichment;
};

export type MediaAnalysisPayload = {
  analysis?: string;
  status?: string;
  error?: string;
  analyzedAt?: string;
  s3Key?: string;
  usage?: { totalTokens?: number };
};

export type CompetitorPostPayload = {
  url?: string;
  caption?: string;
  platform?: string;
  competitor?: string;
  engagement?: Record<string, unknown>;
};

export type CompetitorProfilePayload = {
  bio?: string;
  link?: string;
  handle?: string;
  platform?: string;
  competitor?: string;
  followerCount?: number;
};

export type CompetitorIdentityPayload = {
  brandName?: string;
  instagram?: string;
  tiktok?: string;
  rationale?: string;
};

export type ReportCitationPayload = {
  title?: string;
  gist?: string;
  host?: string;
};

export type ObservationGroup = {
  id: string;
  parent: Observation | null;
  children: Observation[];
  standalone: Observation;
};

function asString(value: unknown): string | undefined {
  return typeof value === "string" && value.length > 0 ? value : undefined;
}

function asRecord(value: unknown): Record<string, unknown> | null {
  if (value !== null && typeof value === "object" && !Array.isArray(value)) {
    return value as Record<string, unknown>;
  }
  return null;
}

export function getSourceRefId(sourceRef: unknown): string | null {
  if (typeof sourceRef === "string") return sourceRef;
  const record = asRecord(sourceRef);
  return asString(record?.id) ?? null;
}

export function getEnrichment(payload: Record<string, unknown>): ReferenceEnrichment | null {
  const enrichment = asRecord(payload.enrichment);
  if (!enrichment) return null;
  return {
    status: asString(enrichment.status),
    mediaKind: asString(enrichment.mediaKind),
    analysisObservationId: asString(enrichment.analysisObservationId),
    s3Key: asString(enrichment.s3Key),
    error: asString(enrichment.error),
    updatedAt: asString(enrichment.updatedAt),
  };
}

export function observationMayHaveStoredMedia(type: string): boolean {
  return (
    type === OBSERVATION_TYPES.REFERENCE_CONTENT ||
    type === OBSERVATION_TYPES.REFERENCE_VIDEO_ANALYSIS ||
    type === OBSERVATION_TYPES.REFERENCE_IMAGE_ANALYSIS
  );
}

const ACTIVE_ENRICHMENT_STATUSES = new Set(["pending", "processing"]);

export function hasActiveEnrichmentJobs(observations: Observation[]): boolean {
  return observations.some((obs) => {
    if (obs.type !== OBSERVATION_TYPES.REFERENCE_CONTENT) return false;
    const status = getEnrichment(obs.payload)?.status;
    return status != null && ACTIVE_ENRICHMENT_STATUSES.has(status);
  });
}

function sortObservationsNewestFirst(a: Observation, b: Observation): number {
  return new Date(b.observedAt).getTime() - new Date(a.observedAt).getTime();
}

export function groupObservations(observations: Observation[]): ObservationGroup[] {
  const childTypes = new Set<string>([
    OBSERVATION_TYPES.REFERENCE_VIDEO_ANALYSIS,
    OBSERVATION_TYPES.REFERENCE_IMAGE_ANALYSIS,
  ]);

  const byId = new Map(observations.map((obs) => [obs.id, obs]));
  const childrenByParent = new Map<string, Observation[]>();
  const childIds = new Set<string>();

  for (const obs of observations) {
    if (!childTypes.has(obs.type)) continue;
    const parentId = getSourceRefId(obs.sourceRef);
    if (!parentId || !byId.has(parentId)) continue;
    childIds.add(obs.id);
    const list = childrenByParent.get(parentId) ?? [];
    list.push(obs);
    childrenByParent.set(parentId, list);
  }

  for (const list of childrenByParent.values()) {
    list.sort(sortObservationsNewestFirst);
  }

  const groups: ObservationGroup[] = [];
  const usedIds = new Set<string>();

  for (const obs of observations) {
    if (usedIds.has(obs.id) || childIds.has(obs.id)) continue;

    const children = childrenByParent.get(obs.id) ?? [];
    for (const child of children) usedIds.add(child.id);
    usedIds.add(obs.id);
    groups.push({
      id: obs.id,
      parent: children.length > 0 ? obs : null,
      children,
      standalone: obs,
    });
  }

  return groups.sort(
    (a, b) =>
      new Date(b.standalone.observedAt).getTime() -
      new Date(a.standalone.observedAt).getTime(),
  );
}

export function observationTypeLabel(type: string): string {
  switch (type) {
    case OBSERVATION_TYPES.REFERENCE_CONTENT:
      return "Reference";
    case OBSERVATION_TYPES.REFERENCE_VIDEO_ANALYSIS:
      return "Video analysis";
    case OBSERVATION_TYPES.REFERENCE_IMAGE_ANALYSIS:
      return "Image analysis";
    case OBSERVATION_TYPES.COMPETITOR_POST:
      return "Competitor post";
    case OBSERVATION_TYPES.COMPETITOR_PROFILE:
      return "Competitor profile";
    case OBSERVATION_TYPES.COMPETITOR_IDENTITY:
      return "Competitor discovery";
    case OBSERVATION_TYPES.ECHO_HELLO:
      return "Echo";
    case OBSERVATION_TYPES.REPORT_CITATION:
      return "Source";
    default:
      return type.replace(/[._]/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
  }
}

/** Stable hue 0–359 from any string (observation type slug). */
export function hashStringToHue(seed: string): number {
  let hash = 2166136261;
  for (let i = 0; i < seed.length; i++) {
    hash ^= seed.charCodeAt(i);
    hash = Math.imul(hash, 16777619);
  }
  return (hash >>> 0) % 360;
}

/** Inline badge colors: same `type` always maps to the same hue. */
export function observationTypeBadgeStyle(type: string): CSSProperties {
  const hue = hashStringToHue(type);
  return {
    borderColor: `hsla(${hue}, 48%, 52%, 0.42)`,
    backgroundColor: `hsla(${hue}, 40%, 36%, 0.24)`,
    color: `hsla(${hue}, 62%, 84%, 1)`,
  };
}

export function observationTypeShortLabel(type: string): string {
  switch (type) {
    case OBSERVATION_TYPES.REFERENCE_CONTENT:
      return "Ref";
    case OBSERVATION_TYPES.REFERENCE_VIDEO_ANALYSIS:
      return "Video";
    case OBSERVATION_TYPES.REFERENCE_IMAGE_ANALYSIS:
      return "Image";
    case OBSERVATION_TYPES.COMPETITOR_POST:
      return "Post";
    case OBSERVATION_TYPES.COMPETITOR_PROFILE:
      return "Profile";
    case OBSERVATION_TYPES.COMPETITOR_IDENTITY:
      return "Comp";
    case OBSERVATION_TYPES.ECHO_HELLO:
      return "Echo";
    case OBSERVATION_TYPES.REPORT_CITATION:
      return "Source";
    default:
      return truncate(type.split(".").pop() ?? type, 10);
  }
}

/** Parent first, then children, preserving group order (newest groups first). */
export function flattenObservationGroups(groups: ObservationGroup[]): Observation[] {
  const out: Observation[] = [];
  for (const group of groups) {
    if (group.parent) {
      out.push(group.parent, ...group.children);
    } else {
      out.push(group.standalone);
    }
  }
  return out;
}

export function childCountByParentId(groups: ObservationGroup[]): Map<string, number> {
  const counts = new Map<string, number>();
  for (const group of groups) {
    if (group.parent && group.children.length > 0) {
      counts.set(group.parent.id, group.children.length);
    }
  }
  return counts;
}

export function truncate(text: string, max: number): string {
  if (text.length <= max) return text;
  return `${text.slice(0, max)}…`;
}

function capitalizeWord(value: string): string {
  if (!value) return value;
  return value.charAt(0).toUpperCase() + value.slice(1);
}

function formatCount(value: number): string {
  return value.toLocaleString();
}

function engagementMetric(engagement: Record<string, unknown>, ...keys: string[]): number | null {
  for (const key of keys) {
    const value = engagement[key];
    if (typeof value === "number" && Number.isFinite(value)) return value;
  }
  return null;
}

export function formatCompetitorEngagement(
  engagement?: Record<string, unknown>,
): string | null {
  if (!engagement) return null;
  const parts: string[] = [];
  const views = engagementMetric(engagement, "play_count", "video_view_count");
  const likes = engagementMetric(engagement, "digg_count", "like_count");
  const comments = engagementMetric(engagement, "comment_count");
  const shares = engagementMetric(engagement, "share_count");
  if (views != null) parts.push(`${formatCount(views)} views`);
  if (likes != null) parts.push(`${formatCount(likes)} likes`);
  if (comments != null) parts.push(`${formatCount(comments)} comments`);
  if (shares != null && shares > 0) parts.push(`${formatCount(shares)} shares`);
  return parts.length > 0 ? parts.join(" · ") : null;
}

export function competitorPostSummary(payload: CompetitorPostPayload): string {
  const parts: string[] = [];
  if (payload.competitor) parts.push(payload.competitor);
  if (payload.platform) parts.push(capitalizeWord(payload.platform));
  if (payload.caption) parts.push(truncate(payload.caption.replace(/\s+/g, " ").trim(), 80));
  return parts.join(" · ") || "Competitor post";
}

export function competitorProfileSummary(payload: CompetitorProfilePayload): string {
  const parts: string[] = [];
  if (payload.competitor) parts.push(payload.competitor);
  if (payload.platform) parts.push(capitalizeWord(payload.platform));
  if (payload.handle) parts.push(`@${payload.handle.replace(/^@/, "")}`);
  else if (payload.bio) parts.push(truncate(payload.bio.replace(/\s+/g, " ").trim(), 60));
  return parts.join(" · ") || "Competitor profile";
}

export function competitorIdentitySummary(payload: CompetitorIdentityPayload): string {
  const parts: string[] = [];
  if (payload.brandName) parts.push(payload.brandName);
  if (payload.instagram) parts.push(`@${payload.instagram.replace(/^@/, "")}`);
  if (payload.tiktok) parts.push(`TikTok @${payload.tiktok.replace(/^@/, "")}`);
  return parts.join(" · ") || "Competitor discovery";
}

/** Tab labels in the signal sources modal. */
export function observationSourceTabMeta(item: Observation): { title: string; subtitle: string } {
  const payload = item.payload;
  switch (item.type) {
    case OBSERVATION_TYPES.COMPETITOR_POST: {
      const p = payload as CompetitorPostPayload;
      const platform = p.platform ? capitalizeWord(p.platform) : "Social";
      return {
        title: `${platform} post`,
        subtitle: truncate(p.caption?.replace(/\s+/g, " ").trim() ?? p.competitor ?? "", 52),
      };
    }
    case OBSERVATION_TYPES.COMPETITOR_PROFILE: {
      const p = payload as CompetitorProfilePayload;
      const platform = p.platform ? capitalizeWord(p.platform) : "Social";
      return {
        title: `${platform} profile`,
        subtitle: p.handle
          ? `@${p.handle.replace(/^@/, "")}`
          : truncate(p.competitor ?? p.bio?.replace(/\s+/g, " ").trim() ?? "", 52),
      };
    }
    case OBSERVATION_TYPES.COMPETITOR_IDENTITY: {
      const p = payload as CompetitorIdentityPayload;
      return {
        title: "Discovery",
        subtitle: p.brandName ?? truncate(p.rationale ?? "", 52),
      };
    }
    case OBSERVATION_TYPES.REFERENCE_VIDEO_ANALYSIS:
      return { title: "Video analysis", subtitle: "Stored reference video" };
    case OBSERVATION_TYPES.REFERENCE_IMAGE_ANALYSIS:
      return { title: "Image analysis", subtitle: "Stored reference image" };
    case OBSERVATION_TYPES.REFERENCE_CONTENT: {
      const p = payload as ReferenceContentPayload;
      return {
        title: "Reference",
        subtitle: truncate(p.caption ?? p.relevanceReason ?? p.platform ?? "", 52),
      };
    }
    case OBSERVATION_TYPES.REPORT_CITATION: {
      const c = citationOf(item);
      return {
        title: c.host ?? "Source",
        subtitle: truncate(citationHeadline(c), 52),
      };
    }
    default:
      return {
        title: observationTypeLabel(item.type),
        subtitle: truncate(observationRowSummary(item), 52),
      };
  }
}

export function enrichmentBadgeTone(status: string): string {
  switch (status) {
    case "pending":
      return "border-amber-500/30 bg-amber-500/10 text-amber-200";
    case "processing":
      return "border-sky-500/30 bg-sky-500/10 text-sky-200";
    case "completed":
      return "border-emerald-500/30 bg-emerald-500/10 text-emerald-200";
    case "failed":
      return "border-rose-500/30 bg-rose-500/10 text-rose-200";
    case "not_applicable":
      return "border-white/12 bg-white/[0.04] text-white/45";
    default:
      return "border-white/12 bg-white/[0.04] text-white/60";
  }
}

export function referenceContentSummary(payload: ReferenceContentPayload): string {
  const parts: string[] = [];
  if (payload.platform) parts.push(payload.platform);
  if (payload.mediaType) parts.push(payload.mediaType);
  if (payload.author) parts.push(`@${payload.author.replace(/^@/, "")}`);
  if (payload.relevanceReason) parts.push(truncate(payload.relevanceReason, 80));
  else if (payload.caption) parts.push(truncate(payload.caption, 80));
  return parts.join(" · ") || "Reference content";
}

export function analysisSummary(payload: MediaAnalysisPayload): string {
  if (payload.status === "failed" && payload.error) {
    return `Failed: ${truncate(payload.error, 80)}`;
  }
  if (payload.analysis) return truncate(payload.analysis.replace(/\s+/g, " ").trim(), 100);
  return "Analysis result";
}

export function referenceContentDetail(payload: ReferenceContentPayload): string {
  const parts: string[] = [];
  if (payload.platform) parts.push(payload.platform);
  if (payload.mediaType) parts.push(payload.mediaType);
  if (payload.author) parts.push(`@${payload.author.replace(/^@/, "")}`);
  if (payload.relevanceReason) parts.push(payload.relevanceReason);
  else if (payload.caption) parts.push(payload.caption);
  return parts.join(" · ") || "Reference content";
}

export function analysisDetail(payload: MediaAnalysisPayload): string {
  if (payload.status === "failed" && payload.error) {
    return `Failed: ${payload.error}`;
  }
  if (payload.analysis) return payload.analysis.trim();
  return "Analysis result";
}

/** Full evidence text for modals / detail panels (no truncation). */
export function observationEvidenceDetail(item: Observation): string {
  const payload = item.payload;
  switch (item.type) {
    case OBSERVATION_TYPES.REFERENCE_CONTENT:
      return referenceContentDetail(payload as ReferenceContentPayload);
    case OBSERVATION_TYPES.REFERENCE_VIDEO_ANALYSIS:
    case OBSERVATION_TYPES.REFERENCE_IMAGE_ANALYSIS:
      return analysisDetail(payload as MediaAnalysisPayload);
    case OBSERVATION_TYPES.COMPETITOR_POST:
      return competitorPostDetail(payload as CompetitorPostPayload);
    case OBSERVATION_TYPES.COMPETITOR_PROFILE:
      return competitorProfileDetail(payload as CompetitorProfilePayload);
    case OBSERVATION_TYPES.COMPETITOR_IDENTITY:
      return competitorIdentityDetail(payload as CompetitorIdentityPayload);
    case OBSERVATION_TYPES.REPORT_CITATION: {
      const c = citationOf(item);
      return c.gist || citationHeadline(c);
    }
    default:
      return observationTypeLabel(item.type);
  }
}

export function competitorPostDetail(payload: CompetitorPostPayload): string {
  const lines: string[] = [];
  if (payload.competitor) lines.push(`Competitor: ${payload.competitor}`);
  if (payload.platform) lines.push(`Platform: ${capitalizeWord(payload.platform)}`);
  const engagement = formatCompetitorEngagement(payload.engagement);
  if (engagement) lines.push(`Engagement: ${engagement}`);
  if (payload.caption) {
    lines.push("");
    lines.push("Caption:");
    lines.push(payload.caption.trim());
  }
  return lines.join("\n") || "Competitor post";
}

export function competitorProfileDetail(payload: CompetitorProfilePayload): string {
  const lines: string[] = [];
  if (payload.competitor) lines.push(`Competitor: ${payload.competitor}`);
  if (payload.platform) lines.push(`Platform: ${capitalizeWord(payload.platform)}`);
  if (payload.handle) lines.push(`Handle: @${payload.handle.replace(/^@/, "")}`);
  if (typeof payload.followerCount === "number" && Number.isFinite(payload.followerCount)) {
    lines.push(`Followers: ${formatCount(payload.followerCount)}`);
  }
  if (payload.link) lines.push(`Website: ${payload.link}`);
  if (payload.bio) {
    lines.push("");
    lines.push("Bio:");
    lines.push(payload.bio.trim());
  }
  return lines.join("\n") || "Competitor profile";
}

export function competitorIdentityDetail(payload: CompetitorIdentityPayload): string {
  const lines: string[] = [];
  if (payload.brandName) lines.push(`Brand: ${payload.brandName}`);
  if (payload.instagram) lines.push(`Instagram: @${payload.instagram.replace(/^@/, "")}`);
  if (payload.tiktok) lines.push(`TikTok: @${payload.tiktok.replace(/^@/, "")}`);
  if (payload.rationale) {
    lines.push("");
    lines.push("Rationale:");
    lines.push(payload.rationale.trim());
  }
  return lines.join("\n") || "Competitor discovery";
}

export function observationRowSummary(item: Observation): string {
  const payload = item.payload;
  switch (item.type) {
    case OBSERVATION_TYPES.REFERENCE_CONTENT:
      return referenceContentSummary(payload as ReferenceContentPayload);
    case OBSERVATION_TYPES.REFERENCE_VIDEO_ANALYSIS:
    case OBSERVATION_TYPES.REFERENCE_IMAGE_ANALYSIS:
      return analysisSummary(payload as MediaAnalysisPayload);
    case OBSERVATION_TYPES.COMPETITOR_POST:
      return competitorPostSummary(payload as CompetitorPostPayload);
    case OBSERVATION_TYPES.COMPETITOR_PROFILE:
      return competitorProfileSummary(payload as CompetitorProfilePayload);
    case OBSERVATION_TYPES.COMPETITOR_IDENTITY:
      return competitorIdentitySummary(payload as CompetitorIdentityPayload);
    case OBSERVATION_TYPES.REPORT_CITATION:
      return truncate(citationHeadline(citationOf(item)), 100);
    default:
      return observationTypeLabel(item.type);
  }
}

const CARD_PREVIEW_SKIP = new Set(["Reference content", "Analysis result"]);

/** Grid card body: omit when it would repeat the type badge or a generic placeholder. */
export function observationCardPreview(item: Observation): string | null {
  const summary = observationRowSummary(item).trim();
  if (!summary || CARD_PREVIEW_SKIP.has(summary)) return null;
  if (summary === item.type || summary === observationTypeLabel(item.type)) return null;
  return summary;
}

export function observationReads(item: Observation): number {
  return typeof item.reads === "number" && Number.isFinite(item.reads) ? item.reads : 0;
}

/** Whole days since observedAt, e.g. "0d", "1d". */
export function formatObservationAgeDays(observedAt: string, nowMs = Date.now()): string {
  const days = Math.max(0, Math.floor((nowMs - new Date(observedAt).getTime()) / 86_400_000));
  return `${days}d`;
}

/* ------------------------------------------------------------- citations */

/** One source as the Signals surface prints it, whatever generation of evidence it came from. */
export type Citation = {
  /** Bare hostname ("khaleejtimes.com"), or null when nothing names one. */
  host: string | null;
  /** A real headline, or null when the only title on record is a slug or the host. */
  title: string | null;
  /** What the source says, often a caption and often in Arabic. May be empty. */
  gist: string;
  url: string | null;
  observedAt: string;
};

export function hostOfUrl(url: string | null | undefined): string | null {
  if (!url) return null;
  try {
    return new URL(url).hostname.replace(/^www\./, "");
  } catch {
    return null;
  }
}

const SLUG_TITLE = /^[a-z_]+(\.[a-z_]+)+$/;

/**
 * A title that is not a title: an observation-type slug ("brand.post",
 * "competitor.post", "reference.content"), the host again, or the url the
 * backend falls back to when a citation carried no title of its own. Any of
 * these printed as a headline is noise, so they read as "no title" and the
 * gist carries the row instead.
 */
export function isSlugTitle(title: string, host: string | null, url: string | null): boolean {
  const t = title.trim();
  if (!t) return true;
  if (SLUG_TITLE.test(t)) return true;
  const bare = t.replace(/^https?:\/\//, "").replace(/^www\./, "").replace(/\/$/, "");
  if (host && (t === host || bare === host)) return true;
  if (url && (t === url || bare === url.replace(/^https?:\/\//, "").replace(/^www\./, "").replace(/\/$/, ""))) {
    return true;
  }
  return false;
}

/** The way out to a source, named for where it lands: the platform for a
 *  TikTok or Instagram post, "the article" for everything else. */
export function openSourceLabel(host: string | null): string {
  const h = host?.toLowerCase() ?? "";
  if (h.includes("tiktok")) return "Open on TikTok";
  if (h.includes("instagram")) return "Open on Instagram";
  return "Open the article";
}

/** The best single line for a source: its headline, else its gist, else its host. */
export function citationHeadline(c: Citation): string {
  return c.title ?? (c.gist.replace(/\s+/g, " ").trim() || c.host || "Source");
}

/**
 * Normalise any grounding observation into one citation. `report.citation` is
 * what the field reporter writes today; the older observation types (competitor
 * posts, references) still ground signals filed before it, so they map onto the
 * same four fields rather than printing their type name as content.
 */
export function citationOf(observation: Observation): Citation {
  const payload = observation.payload ?? {};
  const url = observation.sourceUrl ?? asString(payload.url) ?? null;

  if (observation.type === OBSERVATION_TYPES.REPORT_CITATION) {
    const p = payload as ReportCitationPayload;
    const host = asString(p.host)?.replace(/^www\./, "") ?? hostOfUrl(url);
    const rawTitle = asString(p.title) ?? "";
    return {
      host,
      title: isSlugTitle(rawTitle, host, url) ? null : rawTitle.trim(),
      gist: asString(p.gist)?.trim() ?? "",
      url,
      observedAt: observation.observedAt,
    };
  }

  const host = hostOfUrl(url) ?? asString(payload.platform) ?? null;
  const rawTitle =
    asString(payload.title) ??
    asString(payload.competitor) ??
    asString(payload.brandName) ??
    asString(payload.author) ??
    "";
  const gist =
    asString(payload.gist) ??
    asString(payload.caption) ??
    asString(payload.bio) ??
    asString(payload.rationale) ??
    asString(payload.relevanceReason) ??
    asString(payload.analysis) ??
    "";
  return {
    host,
    title: isSlugTitle(rawTitle, host, url) ? null : rawTitle.trim(),
    gist: gist.trim(),
    url,
    observedAt: observation.observedAt,
  };
}
